import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import CircularProgress from '@mui/material/CircularProgress';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';

const WorkerRosterPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { managementToken } = useManagementAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const dateError = startDate && endDate && startDate > endDate
    ? '開始日は終了日以前に設定してください'
    : null;

  const handleDownload = async () => {
    if (!managementToken || !companyId) return;
    if (dateError) return;
    setDownloading(true);
    setMessage(null);
    try {
      const blob = await api.managementCompany.downloadWorkersCsv(
        companyId, managementToken,
        startDate || undefined,
        endDate || undefined
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'workers_roster.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'ダウンロードが完了しました' });
    } catch {
      setMessage({ type: 'error', text: 'ダウンロードに失敗しました' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
        労働者名簿
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        労働者（クルー）の名簿・支払い情報を期間を指定してダウンロードできます。
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardHeader
          avatar={<PeopleIcon color="primary" />}
          title="ダウンロード条件"
        />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="開始日"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              error={!!dateError}
            />
            <Typography color="text.secondary">〜</Typography>
            <TextField
              label="終了日"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              error={!!dateError}
              helperText={dateError}
            />
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading || !!dateError}
            >
              CSVダウンロード
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            ※期間を指定しない場合、全期間のデータがダウンロードされます
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkerRosterPage;
